import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useLocalStorage } from "react-use";

import { CaseDiagnosis, CptInput } from "custom-models";

import { PathologyWatchButton } from "components/customInputs/buttons/Button";
import PermanentlyResizableTextarea from "components/viewer/PermanentlyResizableTextarea";

import {
  cptString,
  createApiCptList,
  reduceCptCodes,
} from "../DiagnosisDialogHelpers";
import CPTInputGroup from "../dialog-inputs/CPTInputGroup";
import ICD10Input from "../dialog-inputs/ICD10Input";
import ZSDInput from "../dialog-inputs/ZSDInput";
import Sidebar from "../Sidebar";
import SidebarFooter from "../SidebarFooter";
import { createListString, isDigitizationCode } from "../utils";

//TODO: The view is pretty much the same as DiagnosisDialog.tsx. Look into merging these two components.

type ModifyDiagnosisViewProps = {
  hat: string;
  caseId: number;
  addonDiagnosis: Partial<CaseDiagnosis>;
  completeDiagnosis: CaseDiagnosis;
  onUpdateDiagnosis: Function;
  onUpdateDiagnosisCpt: Function;
  onResetDiagnosis?: Function;
  checkDiagnosisDifference?: Function;
  cptCodes: CptInput[];
  setCptCodes: Function;
  displayFlatDiagnosis?: boolean;
  hasScreenDisclaimer?: boolean;
  isCorrection?: boolean;
  isModMedCase?: boolean;
};

const ModifyDiagnosisView = ({
  hat,
  caseId,
  addonDiagnosis,
  completeDiagnosis,
  onUpdateDiagnosis,
  onUpdateDiagnosisCpt,
  onResetDiagnosis,
  checkDiagnosisDifference,
  cptCodes,
  setCptCodes,
  displayFlatDiagnosis,
  hasScreenDisclaimer,
  isCorrection,
}: ModifyDiagnosisViewProps) => {
  const navigate = useNavigate();

  const [commonWidth, setCommonWidth] = useLocalStorage<number | undefined>(
    "DIAGNOSIS_TEXTAREA_WIDTH"
  );

  const [cptError, setCptError] = useState<boolean>(false);
  const [icd10Error, setIcd10Error] = useState<boolean>(false);
  const [zsdError, setZsdError] = useState<boolean>(false);

  const checkErrors = (
    error?: boolean,
    errorType: "zsd" | "icd10" | "cpt" | "" = ""
  ) => {
    return (
      error ||
      (errorType === "zsd" ? error : zsdError) ||
      (errorType === "cpt" ? error : cptError) ||
      (errorType === "icd10" ? error : icd10Error)
    );
  };

  const completeDiagnosisCptStr = reduceCptCodes(
    completeDiagnosis.cpt_codes || []
  )
    .reduce((acc: string[], cptInput) => {
      if (cptInput.code.code) {
        acc.push(`${cptString(cptInput.code)}(${cptInput.quantity || 1})`);
      }
      return acc;
    }, [])
    .join(", ");

  const handleOnResetDiagnosis = () => {
    onResetDiagnosis && onResetDiagnosis();
    setCptCodes(reduceCptCodes(completeDiagnosis.cpt_codes || []));
  };

  const handleOnViewPreview = () => {
    navigate(`/cases/${caseId}/preview`);
  };

  const naturalCptCodes = cptCodes.filter(
    (cptCode) => !isDigitizationCode(cptCode)
  );

  return (
    <Sidebar
      hasAutoWidth
      isScrollable
      hat={
        <div className="FinalizedDiagnosis__header FinalizedDiagnosis__header--edit">
          <FontAwesomeIcon icon="pencil" />
          <span>{hat}</span>
        </div>
      }
    >
      <div className="FinalizedDiagnosis Sidebar__contentContainer">
        <div className="DiagnosisDialog__diagnosis diagnosis__section">
          <label
            className="diagnosis__sectionLabel"
            htmlFor="diagnosis-textarea"
          >
            Diagnosis
          </label>
          {(isCorrection || displayFlatDiagnosis) &&
            completeDiagnosis.diagnosis && (
              <span
                className="readOnly__section"
                style={{ maxWidth: commonWidth }}
              >
                {completeDiagnosis.diagnosis}
              </span>
            )}
          {!isCorrection && (
            <PermanentlyResizableTextarea
              id="diagnosis-textarea"
              rows={5}
              value={addonDiagnosis.diagnosis || ""}
              onChange={(_e) => {
                onUpdateDiagnosis("diagnosis", _e.target.value);
              }}
              commonWidth={commonWidth}
              setCommonWidth={setCommonWidth}
            />
          )}
        </div>
        <div className="DiagnosisDialog__findings diagnosis__section">
          <label
            className="diagnosis__sectionLabel"
            htmlFor="microscopic-textarea"
          >
            Microscopic Findings
          </label>
          {(isCorrection || displayFlatDiagnosis) &&
            completeDiagnosis.microscopic && (
              <span
                className="readOnly__section"
                style={{ maxWidth: commonWidth }}
              >
                {completeDiagnosis.microscopic}
              </span>
            )}
          {!isCorrection && (
            <PermanentlyResizableTextarea
              id="microscopic-textarea"
              rows={5}
              value={addonDiagnosis.microscopic || ""}
              onChange={(_e) => {
                onUpdateDiagnosis("microscopic", _e.target.value);
              }}
              commonWidth={commonWidth}
              setCommonWidth={setCommonWidth}
            />
          )}
          {hasScreenDisclaimer && (
            <label className="checkbox__label" style={{ width: commonWidth }}>
              <input
                type="checkbox"
                //@ts-ignore
                checked={!!addonDiagnosis.is_screen_valid}
                onChange={(_e) => {
                  onUpdateDiagnosis(
                    "is_screen_valid",
                    //@ts-ignore
                    !addonDiagnosis.is_screen_valid
                  );
                }}
              />
              Dr. {completeDiagnosis.user?.first_name}{" "}
              {completeDiagnosis.user?.last_name}, a board certified
              dermatopathologist, has reviewed the case and concurs.
            </label>
          )}
        </div>
        <div className="DiagnosisDialog__comments diagnosis__section">
          <label
            className="diagnosis__sectionLabel"
            htmlFor="comments-textarea"
          >
            Note
          </label>
          {(isCorrection || displayFlatDiagnosis) &&
            completeDiagnosis.comment && (
              <span
                className="readOnly__section"
                style={{ maxWidth: commonWidth }}
              >
                {completeDiagnosis.comment}
              </span>
            )}
          {!isCorrection && (
            <PermanentlyResizableTextarea
              id="comments-textarea"
              rows={5}
              value={addonDiagnosis.comment || ""}
              onChange={(_e) => {
                onUpdateDiagnosis("comment", _e.target.value);
              }}
              commonWidth={commonWidth}
              setCommonWidth={setCommonWidth}
            />
          )}
        </div>
        <div className="DiagnosisDialog__codes">
          <div className="diagnosis__section">
            {displayFlatDiagnosis ? (
              <>
                <label className="diagnosis__sectionLabel">ICD10</label>
                <span className="readOnly__section">
                  {completeDiagnosis.icd10_code}
                </span>
              </>
            ) : (
              <ICD10Input
                icd10={addonDiagnosis.icd10_code || ""}
                icd10Error={icd10Error}
                setIcd10={(newValue: string) => {
                  onUpdateDiagnosis("icd10_code", newValue.toUpperCase());
                }}
                setIcd10Error={setIcd10Error}
              />
            )}
          </div>
          <div className="diagnosis__section">
            {displayFlatDiagnosis ? (
              <>
                <label className="diagnosis__sectionLabel">ICD10</label>
                <span className="readOnly__section">
                  {completeDiagnosis.zsd_code}
                </span>
              </>
            ) : (
              <ZSDInput
                zsd={addonDiagnosis.zsd_code || ""}
                zsdError={zsdError}
                setZsd={(newValue: string) => {
                  onUpdateDiagnosis("zsd_code", newValue);
                }}
                setZsdError={setZsdError}
              />
            )}
          </div>
          <div className="diagnosis__section">
            <label className="diagnosis__sectionLabel">CPT</label>
            {displayFlatDiagnosis && completeDiagnosisCptStr && (
              <span
                className="readOnly__section"
                style={{ maxWidth: commonWidth }}
              >
                {completeDiagnosisCptStr}
              </span>
            )}
            <CPTInputGroup
              cptCodes={naturalCptCodes}
              setCptCodes={(newValue: CptInput[]) => {
                setCptCodes(newValue);
                onUpdateDiagnosisCpt(
                  createApiCptList(newValue.filter((c) => !c.error))
                );
              }}
              setCptError={setCptError}
              displayLabel={false}
            />
          </div>
        </div>
      </div>
      <SidebarFooter>
        <div className="FinalizedDiagnosisActions">
          <span data-testid="error-span" className="diagnosisForm__formError">
            {checkErrors(false) &&
              `Unrecognised ${createListString([
                ...(icd10Error ? ["ICD10"] : []),
                ...(zsdError ? ["ZSD"] : []),
                ...(cptCodes.some((cpt) => cpt.error) ? ["CPT"] : []),
              ])} code`}
          </span>
          {onResetDiagnosis && (
            <PathologyWatchButton
              onClick={() => {
                handleOnResetDiagnosis();
              }}
              disabled={
                checkDiagnosisDifference &&
                !checkDiagnosisDifference(
                  completeDiagnosis,
                  addonDiagnosis,
                  cptCodes
                )
              }
            >
              Reset Diagnosis
            </PathologyWatchButton>
          )}
          <PathologyWatchButton
            color="green"
            onClick={() => {
              handleOnViewPreview();
            }}
          >
            Preview for Sign Out &rarr;
          </PathologyWatchButton>
        </div>
      </SidebarFooter>
    </Sidebar>
  );
};

export default ModifyDiagnosisView;
